import {
  ArrowRightFill18Icon,
  CloseFill18Icon,
} from "@vygruppen/spor-icon-react";
import {
  ActiveTab,
  InActiveTabMain,
  TabText,
} from "features/CenterContent/shared/Tabs/TabsStyle";
import { addOrRemove } from "features/TodoOploggSidebar/Todo/todoFilter/useTodoFilterState";
import { FC, ReactNode, useEffect } from "react";
import { AlertsCounter } from "shared/components/dataDisplay/AlertsCounter";
import { Flex, FlexProps } from "shared/components/Spor";

export type Tabs = {
  id: string;
  iconActive: ReactNode;
  iconInactive: ReactNode;
  text: string;
};

type NotificationMap = { [TabsId: string]: number };

type TabsSidesProps = {
  tabs: Tabs[];
  selectedTabIds: string[];
  setSelectedTabIds: (TabIds: string[]) => void;
  justifyContent: "start" | "end";
  allowSplit?: boolean;
  notifications?: NotificationMap;
  setNotifications?: (value: number[]) => void;
  alwaysShowNotifications?: boolean;
  inModal?: boolean;
  notificationSemantics?: "transparent" | "alarm" | "success";
} & FlexProps;

const showNotification = (
  tabId: string,
  notifications: NotificationMap | undefined,
  alwaysShow: boolean,
  selectedTabIds: string[],
): notifications is NotificationMap => {
  if (alwaysShow) return !!notifications?.[tabId];
  const isSelected = selectedTabIds.includes(tabId);
  const hasNotifications = !!notifications?.[tabId];
  return !isSelected && hasNotifications;
};

export const TabsSides: FC<TabsSidesProps> = ({
  tabs,
  selectedTabIds,
  setSelectedTabIds,
  justifyContent,
  allowSplit,
  notifications,
  setNotifications,
  alwaysShowNotifications,
  inModal = false,
  notificationSemantics = "transparent",
  ...props
}) => {
  const multipleTabs = selectedTabIds.length >= 2;

  useEffect(() => {
    // If you switch Tabs, set each Tabs's notification to 0
    if (setNotifications) setNotifications(tabs.map((_) => 0));
  }, [selectedTabIds]);

  return (
    <Flex
      justifyContent={allowSplit ? "space-between" : justifyContent}
      width="100%"
      mb="0px"
      zIndex="9"
      px="6px"
      {...props}
    >
      {tabs?.map((tab, i) => {
        const isActive = selectedTabIds.includes(tab.id);
        const TabsForRoleItem = isActive ? ActiveTab : InActiveTabMain;
        const SplitIcon = multipleTabs ? CloseFill18Icon : ArrowRightFill18Icon;
        const icon = isActive ? tab.iconActive : tab.iconInactive;
        const action = allowSplit
          ? () => setSelectedTabIds(addOrRemove(selectedTabIds, tab.id))
          : () => setSelectedTabIds([tab.id]);
        const disabled = allowSplit && i === 0;

        return (
          <TabsForRoleItem
            key={tab.id}
            title={`Vis ${tab.text}`}
            onClick={disabled ? undefined : action}
            $disabled={disabled}
            $inModal={inModal}
            style={{ gap: "0px" }}
          >
            <TabText>
              {icon}
              {tab.text}
            </TabText>
            {showNotification(
              tab.id,
              notifications,
              !!alwaysShowNotifications,
              selectedTabIds,
            ) ? (
              <AlertsCounter
                ml="6px"
                alerts={notifications[tab.id] ?? 0}
                semantics={notificationSemantics}
              />
            ) : null}
            {allowSplit && i === 1 && <SplitIcon />}
          </TabsForRoleItem>
        );
      })}
    </Flex>
  );
};
